import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useVoice } from '../hooks/useVoice';
import { useGhost } from '../contexts/GhostContext';
import {
  Menu,
  Search,
  User,
  Mic,
  Plus,
  Zap,
  Ghost,
  Grid,
  Briefcase,
  X,
  ChevronRight,
  ChevronLeft,
  Image as ImageIcon,
  Video,
  FileText,
  Smartphone,
  Users,
  Radio,
  Activity,
  Calendar,
  Mail,
  MessageCircle,
  Share2,
  Globe,
  Layout,
  Cpu,
  Clock,
  Pin,
  Trash2,
  FolderPlus,
  MoreVertical,
  Link,
  Upload,
  Play,
  Pause,
  Check,
  CheckSquare,
  AlertCircle,
  XCircle,
  RefreshCw,
  Edit,
  Send,
  ArrowUp,
  Loader,
  Sparkles,
  Cloud,
  Sun,
  Car,
  TrendingUp,
  Bell,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

// Custom 3-Ring Atom Icon with Nucleus
export const AtomIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 100 100"
    fill="none"
    stroke="currentColor"
    strokeWidth="6"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="50" cy="50" r="10" fill="currentColor" stroke="none" />
    <ellipse cx="50" cy="50" rx="45" ry="14" transform="rotate(0 50 50)" />
    <ellipse cx="50" cy="50" rx="45" ry="14" transform="rotate(60 50 50)" />
    <ellipse cx="50" cy="50" rx="45" ry="14" transform="rotate(120 50 50)" />
  </svg>
);

// Glass Card Component
export const GlassCard = ({ children, className = "", onClick }: { children: React.ReactNode; className?: string; onClick?: () => void }) => (
  <div
    onClick={onClick}
    className={`bg-cyan-500/[0.03] backdrop-blur-[40px] border-[1.5px] border-cyan-400/50 shadow-[0_0_25px_rgba(34,211,238,0.25),0_0_50px_rgba(34,211,238,0.1)] rounded-2xl hover:border-cyan-400/70 hover:shadow-[0_0_35px_rgba(34,211,238,0.4),0_0_70px_rgba(34,211,238,0.2)] transition-all duration-300 ${className}`}
  >
    {children}
  </div>
);

// Sidebar Item Component
export const SidebarItem = ({ icon: Icon, label, active, onClick }: { icon: React.ElementType; label: string; active?: boolean; onClick?: () => void }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center space-x-4 px-4 py-3 rounded-xl transition-all duration-200 border border-transparent ${active ? 'bg-cyan-400/10 text-cyan-300 border-cyan-400/30 shadow-[0_0_15px_rgba(34,211,238,0.15)] font-bold' : 'hover:bg-cyan-400/5 text-cyan-400/60 hover:text-cyan-400 font-medium'}`}
  >
    <Icon className={`w-5 h-5 ${active ? 'drop-shadow-[0_0_8px_rgba(34,211,238,0.8)]' : ''}`} />
    <span className="text-lg tracking-wide">{label}</span>
  </button>
);

// Settings Item Component
export const SettingsItem = ({ label, value, onClick }: { label: string; value?: string; onClick?: () => void }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center justify-between py-4 px-4 border-b border-cyan-400/10 last:border-0 hover:bg-cyan-400/5 transition-colors cursor-pointer group text-left"
  >
    <span className="text-cyan-400/80 group-hover:text-cyan-300 text-base font-medium">{label}</span>
    <div className="flex items-center space-x-2">
      {value && <span className="text-cyan-400 font-semibold text-sm">{value}</span>}
      <ChevronRight className="w-4 h-4 text-cyan-400/40 group-hover:text-cyan-400 transition-colors" />
    </div>
  </button>
);

// Background Component
export const SpaceBackground = () => (
  <div className="absolute inset-0 z-0 overflow-hidden bg-black">
    <div className="absolute top-[-50%] left-[-50%] w-[200%] h-[100%] bg-[radial-gradient(ellipse_at_center,_rgba(255,255,255,0.03)_0%,_transparent_70%)] blur-[80px] pointer-events-none" />
    <div className="absolute bottom-[-50%] right-[-20%] w-[150%] h-[100%] bg-[radial-gradient(ellipse_at_center,_rgba(34,211,238,0.02)_0%,_transparent_70%)] blur-[100px] pointer-events-none" />
    <div className="absolute inset-0 opacity-[0.02] pointer-events-none z-[1]"
      style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }}
    />
  </div>
);

// Header Component
export const Header = ({ onMenuClick }: { onMenuClick: () => void }) => {
  const navigate = useNavigate();

  const handleLogoClick = () => {
    navigate('/');
  };

  return (
    <header className="absolute top-0 left-0 w-full p-4 md:p-6 flex items-center z-20">
      <button
        onClick={onMenuClick}
        className="p-2 md:p-3 rounded-full bg-cyan-500/[0.05] hover:bg-cyan-400/[0.1] backdrop-blur-3xl border border-cyan-400/30 shadow-[0_0_15px_rgba(34,211,238,0.1)] transition-all hover:scale-105 active:scale-95 group mr-3 md:mr-6"
      >
        <Menu className="w-5 h-5 md:w-6 md:h-6 text-cyan-400 group-hover:text-cyan-200 drop-shadow-[0_0_5px_rgba(34,211,238,0.5)]" />
      </button>

      <button onClick={handleLogoClick} className="flex flex-col items-center justify-center hover:opacity-80 transition-opacity cursor-pointer">
        <h1 className="text-xl md:text-2xl lg:text-[2.5rem] font-black tracking-tighter text-cyan-400 mb-1 drop-shadow-[0_0_20px_rgba(34,211,238,0.4)]">
          SUPERJARVIS
        </h1>
        <div className="flex items-center space-x-2">
          <div className="h-[2px] w-3 md:w-4 bg-cyan-400 shadow-[0_0_5px_cyan]" />
          <p className="text-cyan-400 text-[8px] md:text-[10px] tracking-[0.15em] md:tracking-[0.2em] font-bold uppercase drop-shadow-[0_0_8px_rgba(34,211,238,0.8)]">
            Executive AI Companion
          </p>
          <div className="h-[2px] w-3 md:w-4 bg-cyan-400 shadow-[0_0_5px_cyan]" />
        </div>
      </button>

      <button onClick={handleLogoClick} className="ml-auto hover:scale-105 transition-transform cursor-pointer hidden md:block">
        <AtomIcon className="w-12 h-12 text-cyan-400 drop-shadow-[0_0_15px_rgba(34,211,238,0.8)] animate-spin-slow" />
      </button>
    </header>
  );
};

// Chat Bar Component
interface ChatBarProps {
  inputText: string;
  setInputText: (text: string) => void;
  chatMode: string;
  setChatMode: (mode: string) => void;
  onSend?: () => void;
  onGhostClick?: () => void;
  onMicClick?: () => void;
  onFileSelect?: (files: FileList) => void;
  onImageAttach?: (base64: string) => void;
  onLiveMode?: () => void;
  isLoading?: boolean;
}

export const ChatBar = ({ inputText, setInputText, chatMode: _chatMode, setChatMode: _setChatMode, onSend, onGhostClick, onMicClick, onFileSelect, onImageAttach, onLiveMode, isLoading = false }: ChatBarProps) => {
  const navigate = useNavigate();
  const [plusMenuOpen, setPlusMenuOpen] = useState(false);
  // Mode menu removed — AI decides strategy
  const plusMenuRef = useRef<HTMLDivElement>(null);
  // modeMenuRef removed — mode selector removed
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Voice recording hook
  const {
    isRecording,
    isTranscribing,
    toggleRecording
  } = useVoice({
    onTranscription: useCallback((text: string) => {
      setInputText(text);
      textareaRef.current?.focus();
    }, [setInputText]),
    onError: useCallback((error: string) => {
      alert(error);
    }, [])
  });

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      if (onFileSelect) {
        onFileSelect(files);
      }
      // For images, also convert to base64 for preview
      const file = files[0];
      if (file.type.startsWith('image/') && onImageAttach) {
        const reader = new FileReader();
        reader.onload = () => {
          if (typeof reader.result === 'string') {
            onImageAttach(reader.result);
          }
        };
        reader.readAsDataURL(file);
      }
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  // Handle mic button click - use voice hook or external handler
  const handleMicClick = () => {
    if (onMicClick) {
      onMicClick();
    } else {
      toggleRecording();
    }
  };

  // Plus menu item handlers
  const plusMenuItems = [
    {
      icon: FileText,
      label: "Add photos & files",
      onClick: () => { fileInputRef.current?.click(); setPlusMenuOpen(false); }
    },
    {
      icon: ImageIcon,
      label: "Create image",
      onClick: () => {
        setInputText("Create an image of ");
        setPlusMenuOpen(false);
        textareaRef.current?.focus();
      }
    },
    {
      icon: Video,
      label: "Create video",
      onClick: () => {
        setInputText("Create a video of ");
        setPlusMenuOpen(false);
        textareaRef.current?.focus();
      }
    },
    {
      icon: Smartphone,
      label: "Create app",
      onClick: () => { navigate('/apps/create'); setPlusMenuOpen(false); }
    },
    {
      icon: Users,
      label: "Connections",
      onClick: () => { navigate('/apps'); setPlusMenuOpen(false); }
    },
    {
      icon: Ghost,
      label: "Shadow mode",
      onClick: () => {
        if (onGhostClick) onGhostClick();
        setPlusMenuOpen(false);
      }
    },
    {
      icon: Radio,
      label: "Live mode",
      onClick: () => {
        if (onLiveMode) {
          onLiveMode();
        } else {
          navigate('/live');
        }
        setPlusMenuOpen(false);
      }
    }
  ];

  // Reset textarea height when input is cleared (e.g., after sending)
  useEffect(() => {
    if (!inputText && textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [inputText]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (plusMenuRef.current && !plusMenuRef.current.contains(event.target as Node)) {
        setPlusMenuOpen(false);
      }

    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  return (
    <div className="w-full max-w-5xl mb-4 md:mb-6 relative" data-tour="chat-input">
      <GlassCard className="flex items-center p-2 pr-3 md:pr-4 relative z-40 transition-all duration-300 ring-2 ring-cyan-400/40 hover:ring-cyan-400/60 focus-within:ring-cyan-400/80 shadow-[0_0_30px_rgba(34,211,238,0.3),0_0_60px_rgba(34,211,238,0.15)] hover:shadow-[0_0_40px_rgba(34,211,238,0.5),0_0_80px_rgba(34,211,238,0.25)] focus-within:shadow-[0_0_50px_rgba(34,211,238,0.6),0_0_100px_rgba(34,211,238,0.3)]">

        {/* Plus Button */}
        <div className="relative" ref={plusMenuRef}>
          <button
            onClick={() => setPlusMenuOpen(!plusMenuOpen)}
            className={`p-2 md:p-3 rounded-full mr-2 md:mr-3 transition-all duration-300 border border-cyan-400/40 ${plusMenuOpen ? 'bg-cyan-400 rotate-45 text-black shadow-[0_0_35px_rgba(34,211,238,0.8),0_0_60px_rgba(34,211,238,0.4)]' : 'bg-cyan-400/10 hover:bg-cyan-400/20 text-cyan-400 shadow-[0_0_15px_rgba(34,211,238,0.2)] hover:shadow-[0_0_30px_rgba(34,211,238,0.5),0_0_50px_rgba(34,211,238,0.25)] hover:border-cyan-400/70'}`}
          >
            <Plus className="w-5 h-5" />
          </button>

          {plusMenuOpen && (
            <div className="absolute bottom-16 left-0 w-72 bg-black/90 backdrop-blur-[50px] border border-cyan-400/30 rounded-2xl shadow-[0_0_50px_rgba(34,211,238,0.15)] p-2 flex flex-col gap-1 animate-in fade-in slide-in-from-bottom-5 duration-200 origin-bottom-left">
              {plusMenuItems.map((item, i) => (
                <button
                  key={i}
                  onClick={item.onClick}
                  className="flex items-center space-x-4 w-full p-3 rounded-xl hover:bg-cyan-400/10 text-left transition-colors border border-transparent hover:border-cyan-400/20 group"
                >
                  <div className="p-1.5 rounded-lg bg-cyan-400/5 group-hover:bg-cyan-400/20 text-cyan-400/70 group-hover:text-cyan-300 transition-all">
                    <item.icon className="w-4 h-4" />
                  </div>
                  <span className="font-semibold text-cyan-400/80 group-hover:text-cyan-300 text-sm drop-shadow-[0_0_2px_rgba(34,211,238,0.2)]">{item.label}</span>
                </button>
              ))}
            </div>
          )}

          {/* Hidden file input for uploads */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*,.pdf,.doc,.docx,.txt,.md"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>

        {/* Input Field - Auto-expanding textarea */}
        <textarea
          ref={textareaRef}
          value={inputText}
          onChange={(e) => {
            setInputText(e.target.value);
            // Auto-resize textarea
            e.target.style.height = 'auto';
            e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (!isLoading) {
                onSend && onSend();
              }
            }
          }}
          placeholder="What can SuperJarvis do for you?"
          rows={1}
          className="flex-1 bg-transparent border-none outline-none text-cyan-300 placeholder-cyan-400/40 text-base md:text-lg px-2 font-medium drop-shadow-[0_0_5px_rgba(34,211,238,0.2)] resize-none overflow-y-auto min-h-[28px] max-h-[120px] leading-7"
        />

        {/* Right Side Tools */}
        <div className="flex items-center space-x-1 md:space-x-3 pl-2">

          {/* Mode Selector removed — AI decides execution strategy */}

          <div className="w-[1.5px] h-6 bg-cyan-400/20 mx-1 hidden md:block"></div>

          <button
            onClick={handleMicClick}
            disabled={isTranscribing}
            className={`p-2 md:p-2.5 rounded-full transition-all duration-300 border group ${isTranscribing
              ? 'bg-cyan-500/20 border-cyan-400/50 text-cyan-400 shadow-[0_0_20px_rgba(34,211,238,0.4)]'
              : isRecording
                ? 'bg-red-500/20 border-red-400/50 text-red-400 shadow-[0_0_20px_rgba(248,113,113,0.4)] animate-pulse'
                : 'hover:bg-cyan-400/15 text-cyan-400/60 hover:text-cyan-300 border-transparent hover:border-cyan-400/50 hover:shadow-[0_0_20px_rgba(34,211,238,0.4),0_0_40px_rgba(34,211,238,0.2)]'
              }`}
            title={isTranscribing ? 'Transcribing...' : isRecording ? 'Stop recording' : 'Start voice input'}
          >
            {isTranscribing ? (
              <Loader className="w-5 h-5 text-cyan-400 animate-spin" />
            ) : (
              <Mic className={`w-5 h-5 ${isRecording ? 'text-red-400' : 'group-hover:drop-shadow-[0_0_12px_rgba(34,211,238,0.9)]'}`} />
            )}
          </button>
          <button
            onClick={() => onLiveMode ? onLiveMode() : navigate('/live')}
            className="p-2 md:p-2.5 rounded-full hover:bg-cyan-400/15 text-cyan-400/60 hover:text-cyan-300 transition-all duration-300 border border-transparent hover:border-cyan-400/50 hover:shadow-[0_0_20px_rgba(34,211,238,0.4),0_0_40px_rgba(34,211,238,0.2)] group hidden md:block"
            title="Live mode - Real-time voice conversation"
          >
            <Radio className="w-5 h-5 group-hover:drop-shadow-[0_0_12px_rgba(34,211,238,0.9)]" />
          </button>
          <button
            onClick={onGhostClick}
            className="p-2 md:p-2.5 rounded-full hover:bg-cyan-400/15 text-cyan-400/60 hover:text-cyan-300 transition-all duration-300 border border-transparent hover:border-cyan-400/50 hover:shadow-[0_0_20px_rgba(34,211,238,0.4),0_0_40px_rgba(34,211,238,0.2)] group hidden md:block"
          >
            <Ghost className="w-5 h-5 group-hover:drop-shadow-[0_0_12px_rgba(34,211,238,0.9)]" />
          </button>

          <div className="w-[1.5px] h-6 bg-cyan-400/20 mx-1 hidden md:block"></div>

          {/* Send Arrow Button */}
          <button
            onClick={onSend}
            disabled={!inputText.trim() || isLoading}
            className={`p-2 md:p-2.5 rounded-full transition-all duration-300 border ${inputText.trim() && !isLoading ? 'bg-cyan-400 text-black border-cyan-400 shadow-[0_0_25px_rgba(34,211,238,0.6),0_0_50px_rgba(34,211,238,0.3)] hover:shadow-[0_0_35px_rgba(34,211,238,0.8),0_0_70px_rgba(34,211,238,0.4)] hover:scale-105' : 'bg-cyan-400/10 text-cyan-400/40 border-cyan-400/20 cursor-not-allowed'}`}
          >
            <ArrowUp className="w-5 h-5" />
          </button>
        </div>
      </GlassCard>
    </div>
  );
};

// Bottom Navigation Component
export const BottomNav = ({ activeTab }: { activeTab?: string }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const tabs = [
    { id: 'recent', label: 'Recent', icon: Clock, path: '/recent', tourId: 'nav-recent' },
    { id: 'tasks', label: 'My Tasks', icon: Activity, path: '/tasks', tourId: 'nav-tasks' },
    { id: 'apps', label: 'My Apps', icon: Grid, path: '/apps', tourId: 'nav-apps' },
    { id: 'projects', label: 'Projects', icon: Briefcase, path: '/projects', tourId: 'nav-projects' },
  ];

  const currentTab = activeTab || tabs.find(t => t.path === location.pathname)?.id || '';

  return (
    <div className="w-full max-w-lg flex justify-between items-end pb-2 px-2">
      {tabs.map(tab => (
        <button
          key={tab.id}
          data-tour={tab.tourId}
          onClick={() => navigate(tab.path)}
          className={`flex flex-col items-center space-y-1 md:space-y-2 group ${currentTab === tab.id ? '' : ''}`}
        >
          <div className={`p-2.5 md:p-3.5 rounded-xl md:rounded-2xl backdrop-blur-2xl border transition-all duration-300 ${currentTab === tab.id
            ? 'bg-cyan-400/20 border-cyan-400/60 shadow-[0_0_25px_rgba(34,211,238,0.4),0_0_50px_rgba(34,211,238,0.2)]'
            : 'bg-cyan-400/5 border-cyan-400/20 group-hover:bg-cyan-400/15 group-hover:border-cyan-400/60 shadow-[0_0_10px_rgba(34,211,238,0.05)] group-hover:shadow-[0_0_25px_rgba(34,211,238,0.4),0_0_50px_rgba(34,211,238,0.2)]'
            }`}>
            <tab.icon className={`w-5 h-5 md:w-6 md:h-6 transition-colors ${currentTab === tab.id
              ? 'text-cyan-300 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)]'
              : 'text-cyan-400/60 group-hover:text-cyan-300 group-hover:drop-shadow-[0_0_10px_rgba(34,211,238,0.8)]'
              }`} />
          </div>
          <span className={`text-[9px] md:text-[11px] font-bold tracking-wider md:tracking-widest uppercase transition-all ${currentTab === tab.id
            ? 'text-cyan-300 drop-shadow-[0_0_8px_rgba(34,211,238,0.6)]'
            : 'text-cyan-400/50 group-hover:text-cyan-300 group-hover:drop-shadow-[0_0_8px_rgba(34,211,238,0.6)]'
            }`}>{tab.label}</span>
        </button>
      ))}
    </div>
  );
};

// Side Navigation Component - Vertical layout for chat mode (same size as BottomNav)
export const SideNav = ({ activeTab }: { activeTab?: string }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const tabs = [
    { id: 'recent', label: 'Recent', icon: Clock, path: '/recent', tourId: 'sidenav-recent' },
    { id: 'tasks', label: 'My Tasks', icon: Activity, path: '/tasks', tourId: 'sidenav-tasks' },
    { id: 'apps', label: 'My Apps', icon: Grid, path: '/apps', tourId: 'sidenav-apps' },
    { id: 'projects', label: 'Projects', icon: Briefcase, path: '/projects', tourId: 'sidenav-projects' },
  ];

  const currentTab = activeTab || tabs.find(t => t.path === location.pathname)?.id || '';

  return (
    <div className="flex flex-col justify-end space-y-2 md:space-y-3">
      {/* New Chat Button */}
      <button
        onClick={() => navigate('/')}
        className="flex flex-col items-center space-y-1 md:space-y-2 group"
      >
        <div className="p-2.5 md:p-3.5 rounded-xl md:rounded-2xl backdrop-blur-2xl border transition-all duration-300 bg-cyan-400/5 border-cyan-400/20 group-hover:bg-cyan-400/15 group-hover:border-cyan-400/60 shadow-[0_0_10px_rgba(34,211,238,0.05)] group-hover:shadow-[0_0_25px_rgba(34,211,238,0.4),0_0_50px_rgba(34,211,238,0.2)]">
          <MessageCircle className="w-5 h-5 md:w-6 md:h-6 transition-colors text-cyan-400/60 group-hover:text-cyan-300 group-hover:drop-shadow-[0_0_10px_rgba(34,211,238,0.8)]" />
        </div>
        <span className="text-[9px] md:text-[11px] font-bold tracking-wider md:tracking-widest uppercase transition-all text-cyan-400/50 group-hover:text-cyan-300 group-hover:drop-shadow-[0_0_8px_rgba(34,211,238,0.6)]">New Chat</span>
      </button>

      {tabs.map(tab => (
        <button
          key={tab.id}
          onClick={() => navigate(tab.path)}
          className={`flex flex-col items-center space-y-1 md:space-y-2 group`}
        >
          <div className={`p-2.5 md:p-3.5 rounded-xl md:rounded-2xl backdrop-blur-2xl border transition-all duration-300 ${currentTab === tab.id
            ? 'bg-cyan-400/20 border-cyan-400/60 shadow-[0_0_25px_rgba(34,211,238,0.4),0_0_50px_rgba(34,211,238,0.2)]'
            : 'bg-cyan-400/5 border-cyan-400/20 group-hover:bg-cyan-400/15 group-hover:border-cyan-400/60 shadow-[0_0_10px_rgba(34,211,238,0.05)] group-hover:shadow-[0_0_25px_rgba(34,211,238,0.4),0_0_50px_rgba(34,211,238,0.2)]'
            }`}>
            <tab.icon className={`w-5 h-5 md:w-6 md:h-6 transition-colors ${currentTab === tab.id
              ? 'text-cyan-300 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)]'
              : 'text-cyan-400/60 group-hover:text-cyan-300 group-hover:drop-shadow-[0_0_10px_rgba(34,211,238,0.8)]'
              }`} />
          </div>
          <span className={`text-[9px] md:text-[11px] font-bold tracking-wider md:tracking-widest uppercase transition-all ${currentTab === tab.id
            ? 'text-cyan-300 drop-shadow-[0_0_8px_rgba(34,211,238,0.6)]'
            : 'text-cyan-400/50 group-hover:text-cyan-300 group-hover:drop-shadow-[0_0_8px_rgba(34,211,238,0.6)]'
            }`}>{tab.label}</span>
        </button>
      ))}
    </div>
  );
};

// Sidebar Component
interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onSettingsClick: () => void;
  userName?: string | null;
  userPhoto?: string | null;
}

export const Sidebar = ({ isOpen, onClose, onSettingsClick, userName, userPhoto }: SidebarProps) => {
  const navigate = useNavigate();
  const displayName = userName || 'Guest';

  return (
    <div
      className={`fixed inset-0 z-50 pointer-events-none transition-all duration-700 ${isOpen ? 'bg-black/80 backdrop-blur-sm pointer-events-auto' : ''}`}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`absolute top-0 left-0 h-full w-[320px] bg-black/95 backdrop-blur-[60px] border-r border-cyan-400/20 shadow-[0_0_80px_rgba(34,211,238,0.1)] transition-transform duration-500 ease-out ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        {/* Sidebar Header */}
        <div className="p-6 border-b border-cyan-400/10 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <AtomIcon className="w-8 h-8 text-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)] animate-spin-slow" />
            <span className="text-xl font-bold text-cyan-400 tracking-tight drop-shadow-[0_0_5px_rgba(34,211,238,0.5)]">SuperJarvis</span>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-cyan-400/10 text-cyan-400/60 hover:text-cyan-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Sidebar Content */}
        <div className="p-4 space-y-1">
          <SidebarItem icon={Activity} label="Current tasks" onClick={() => { navigate('/tasks'); onClose(); }} />
          <SidebarItem
            icon={Calendar}
            label="Calendar"
            onClick={() => {
              onClose();
              // Force full navigation so useSearchParams re-fires even when already on /
              window.location.href = '/?message=' + encodeURIComponent('Show me my calendar for today and this week');
            }}
          />
          <SidebarItem
            icon={Mail}
            label="Email"
            onClick={() => {
              onClose();
              window.location.href = '/?message=' + encodeURIComponent('Show me my latest emails');
            }}
          />
          <SidebarItem
            icon={MessageCircle}
            label="Messages"
            onClick={() => {
              onClose();
              window.location.href = '/?message=' + encodeURIComponent('Show me my recent messages');
            }}
          />
          <SidebarItem
            icon={Share2}
            label="Social Media"
            onClick={() => {
              onClose();
              window.location.href = '/?message=' + encodeURIComponent('Show me my social media updates');
            }}
          />
        </div>

        {/* Divider */}
        <div className="mx-4 h-[1px] bg-cyan-400/20" />

        {/* Projects, Apps, Tasks Section */}
        <div className="p-4 space-y-1">
          <div className="flex items-center justify-between">
            <SidebarItem icon={Briefcase} label="Projects" onClick={() => { navigate('/projects'); onClose(); }} />
            <button
              onClick={() => { navigate('/projects?new=true'); onClose(); }}
              className="p-2 rounded-full bg-cyan-400/10 hover:bg-cyan-400/20 border border-cyan-400/30 hover:border-cyan-400/50 transition-all mr-4"
            >
              <Plus className="w-4 h-4 text-cyan-400" />
            </button>
          </div>
          <div className="flex items-center justify-between">
            <SidebarItem icon={Grid} label="My Apps" onClick={() => { navigate('/apps'); onClose(); }} />
            <button
              onClick={() => { navigate('/apps/create'); onClose(); }}
              className="p-2 rounded-full bg-cyan-400/10 hover:bg-cyan-400/20 border border-cyan-400/30 hover:border-cyan-400/50 transition-all mr-4"
            >
              <Plus className="w-4 h-4 text-cyan-400" />
            </button>
          </div>
          <div className="flex items-center justify-between">
            <SidebarItem icon={Activity} label="My Tasks" onClick={() => { navigate('/tasks'); onClose(); }} />
            <button
              onClick={() => { navigate('/tasks?new=true'); onClose(); }}
              className="p-2 rounded-full bg-cyan-400/10 hover:bg-cyan-400/20 border border-cyan-400/30 hover:border-cyan-400/50 transition-all mr-4"
            >
              <Plus className="w-4 h-4 text-cyan-400" />
            </button>
          </div>
        </div>

        {/* Divider */}
        <div className="mx-4 h-[1px] bg-cyan-400/20" />

        {/* New Chat + All Chats / Recent */}
        <div className="p-4 space-y-1">
          <button
            onClick={() => { navigate('/'); onClose(); }}
            className="w-full flex items-center justify-center space-x-3 px-4 py-3 mb-2 rounded-xl bg-cyan-400/10 hover:bg-cyan-400/20 border border-cyan-400/40 hover:border-cyan-400/60 text-cyan-400 hover:text-cyan-300 transition-all duration-200 font-semibold shadow-[0_0_15px_rgba(34,211,238,0.15)] hover:shadow-[0_0_25px_rgba(34,211,238,0.3)]"
          >
            <Plus className="w-5 h-5" />
            <span className="text-base tracking-wide">New Chat</span>
          </button>
          <SidebarItem icon={Clock} label="Recents" onClick={() => { navigate('/recent'); onClose(); }} />
        </div>

        {/* User Profile */}
        <div className="absolute bottom-0 left-0 w-full p-4 border-t border-cyan-400/10">
          <button
            onClick={onSettingsClick}
            className="w-full flex items-center space-x-4 p-4 rounded-xl hover:bg-cyan-400/5 transition-colors group"
          >
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-400/30 to-cyan-600/30 flex items-center justify-center border border-cyan-400/30 shadow-[0_0_20px_rgba(34,211,238,0.2)] overflow-hidden">
              {userPhoto ? (
                <img src={userPhoto} alt={displayName} className="w-full h-full object-cover" />
              ) : (
                <User className="w-6 h-6 text-cyan-400" />
              )}
            </div>
            <div className="flex-1 text-left">
              <p className="text-cyan-300 font-bold text-base drop-shadow-[0_0_3px_rgba(34,211,238,0.3)]">{displayName}</p>
              <p className="text-cyan-400/50 text-xs">Settings</p>
            </div>
            <ChevronRight className="w-5 h-5 text-cyan-400/30 group-hover:text-cyan-400" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Settings Panel Component
interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSignOut?: () => void;
}

// Appearance Mode Toggle Button
const AppearanceToggle = ({
  mode,
  activeMode,
  onClick
}: {
  mode: string;
  activeMode: string;
  onClick: (mode: string) => void;
}) => {
  const isActive = mode === activeMode;
  return (
    <button
      onClick={() => onClick(mode)}
      className={`flex-1 py-3 px-6 rounded-xl text-sm font-bold tracking-wide transition-all duration-300 ${isActive
        ? 'bg-cyan-400/20 text-cyan-300 border border-cyan-400/50 shadow-[0_0_20px_rgba(34,211,238,0.3)]'
        : 'text-cyan-400/50 hover:text-cyan-400/80 hover:bg-cyan-400/5'
        }`}
    >
      {mode}
    </button>
  );
};

export const SettingsPanel = ({ isOpen, onClose, onSignOut }: SettingsPanelProps) => {
  const navigate = useNavigate();
  const [appearanceMode, setAppearanceMode] = useState('Dark');
  const [notifications, setNotifications] = useState(true);
  const [memory, setMemory] = useState(true);

  if (!isOpen) return null;

  const handleReportBug = () => {
    window.location.href = 'mailto:support@superjarvis.ai?subject=Bug%20Report%20-%20SuperJarvis';
  };

  const handleSignOut = () => {
    if (onSignOut) {
      onSignOut();
      onClose();
    }
  };

  // Navigation handlers for settings items
  const handleSubscription = () => {
    navigate('/settings/subscription');
    onClose();
  };

  const handlePrivacy = () => {
    navigate('/settings/privacy');
    onClose();
  };

  const handleSecurity = () => {
    navigate('/settings/security');
    onClose();
  };

  const handleLanguage = () => {
    navigate('/settings/language');
    onClose();
  };

  const handleTerms = () => {
    navigate('/terms');
    onClose();
  };

  const handlePrivacyPolicy = () => {
    navigate('/privacy');
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-black/95 backdrop-blur-[60px] border border-cyan-400/30 rounded-3xl shadow-[0_0_60px_rgba(34,211,238,0.15)] overflow-hidden"
      >
        {/* Settings Header */}
        <div className="p-6 pb-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,0.5)]">Settings</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-cyan-400/10 text-cyan-400/60 hover:text-cyan-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Settings Content */}
        <div className="px-4 pb-6 max-h-[70vh] overflow-y-auto">

          {/* ACCOUNT Section */}
          <div className="mb-6">
            <h3 className="text-xs font-bold text-cyan-400/50 uppercase tracking-[0.2em] mb-3 px-2">Account</h3>
            <div className="bg-cyan-400/[0.03] rounded-2xl border border-cyan-400/20 overflow-hidden">
              <SettingsItem label="Subscription" value="Super Pro" onClick={handleSubscription} />
              <SettingsItem label="Notifications" value={notifications ? "On" : "Off"} onClick={() => setNotifications(!notifications)} />
              <SettingsItem label="Memory" value={memory ? "On" : "Off"} onClick={() => setMemory(!memory)} />
              <SettingsItem label="Privacy" onClick={handlePrivacy} />
              <SettingsItem label="Security" onClick={handleSecurity} />
              <SettingsItem label="App Language" value="English (US)" onClick={handleLanguage} />
            </div>
          </div>

          {/* APPEARANCE Section — Dark mode only for launch */}
          <div className="mb-6">
            <h3 className="text-xs font-bold text-cyan-400/50 uppercase tracking-[0.2em] mb-3 px-2">Appearance</h3>
            <div className="bg-cyan-400/[0.03] rounded-2xl border border-cyan-400/20 p-2 flex gap-2">
              <div className="flex-1 py-3 px-6 rounded-xl text-sm font-bold tracking-wide opacity-50 text-cyan-400/50 cursor-not-allowed text-center" title="Coming soon">System</div>
              <div className="flex-1 py-3 px-6 rounded-xl text-sm font-bold tracking-wide opacity-50 text-cyan-400/50 cursor-not-allowed text-center" title="Coming soon">Glass</div>
              <AppearanceToggle mode="Dark" activeMode={appearanceMode} onClick={setAppearanceMode} />
            </div>
          </div>

          {/* SUPPORT Section */}
          <div className="mb-6">
            <h3 className="text-xs font-bold text-cyan-400/50 uppercase tracking-[0.2em] mb-3 px-2">Support</h3>
            <div className="bg-cyan-400/[0.03] rounded-2xl border border-cyan-400/20 overflow-hidden">
              <SettingsItem label="Report bug" onClick={handleReportBug} />
              <SettingsItem label="Terms of Use" onClick={handleTerms} />
              <SettingsItem label="Privacy Policy" onClick={handlePrivacyPolicy} />
            </div>
          </div>

          {/* Sign Out Button */}
          {onSignOut && (
            <div className="mb-6">
              <button
                onClick={handleSignOut}
                className="w-full py-3 px-4 rounded-xl bg-red-500/10 border border-red-400/30 text-red-400 font-semibold hover:bg-red-500/20 hover:border-red-400/50 transition-all duration-300"
              >
                Sign Out
              </button>
            </div>
          )}

          {/* Version */}
          <div className="text-center py-4">
            <p className="text-cyan-400/30 text-xs tracking-wider">
              SUPERJARVIS v4.0.0 (AI-First Architecture)
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Page Title Component
export const PageTitle = ({ title, icon }: { title: string; icon?: React.ReactNode }) => (
  <div className="flex items-center space-x-3 mb-6 animate-in fade-in slide-in-from-bottom-2 duration-700">
    {icon ? <span className="text-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)]">{icon}</span> : <AtomIcon className="w-10 h-10 text-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)] animate-spin-slow" />}
    <span className="text-3xl font-bold text-cyan-400 tracking-tight drop-shadow-[0_0_5px_rgba(34,211,238,0.5)]">
      {title}
    </span>
  </div>
);

// Ghost/Shadow Mode Spotlight Overlay
interface GhostSpotlightProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (text: string) => void;
}

export const GhostSpotlight = ({ isOpen, onClose, onSubmit }: GhostSpotlightProps) => {
  const { isRecordingShadow, stopShadowRecord } = useGhost();
  const [inputText, setInputText] = useState('');
  const [_chatMode, _setChatMode] = useState('Super');
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [shadowEvents, setShadowEvents] = useState<any[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input and capture screen when opened
  useEffect(() => {
    if (isOpen) {
      if (inputRef.current) inputRef.current.focus();
      const isElectron = typeof window !== 'undefined' && !!(window as any).wispr?.isElectron;
      if (isElectron) {
        (window as any).wispr.captureScreen().then((res: any) => {
          if (res.success && res.image) {
            setScreenshot(res.image);
          }
        }).catch(console.error);
      }
    } else {
      setScreenshot(null);
      setInputText('');
    }
  }, [isOpen]);

  // Record synthetic DOM interactions when Shadow Mode is active
  useEffect(() => {
    if (!isRecordingShadow) return;

    const handleGlobalClick = (e: MouseEvent) => {
      // Don't record clicks inside the GhostSpotlight UI itself
      const rawTarget = e.target as HTMLElement;
      if (rawTarget.closest('.ghost-spotlight-ui')) return;

      const semanticTarget = rawTarget.closest('button, a, [role="button"], input, textarea, select, label') as HTMLElement | null;
      const target = semanticTarget || rawTarget;
      const rect = target.getBoundingClientRect();
      const selector = target.tagName.toLowerCase() + (target.id ? `#${target.id}` : '') + (target.className ? `.${target.className.split(' ').join('.')}` : '');
      const role = target.getAttribute('role') || target.tagName.toLowerCase();
      const label = (
        target.getAttribute('aria-label') ||
        target.getAttribute('title') ||
        target.getAttribute('placeholder') ||
        target.textContent ||
        rawTarget.textContent ||
        ''
      ).replace(/\s+/g, ' ').trim().slice(0, 120);

      setShadowEvents(prev => [...prev, {
        type: 'click',
        selector,
        target: selector,
        label,
        tag: target.tagName.toLowerCase(),
        target_meta: {
          role,
          title: label,
          tag: target.tagName.toLowerCase(),
          bounds: {
            x: Math.round(rect.x),
            y: Math.round(rect.y),
            width: Math.round(rect.width),
            height: Math.round(rect.height),
          },
          page: {
            url: window.location.href,
            title: document.title,
          },
        },
        timestamp: Date.now()
      }]);
    };

    window.addEventListener('click', handleGlobalClick, true);
    return () => window.removeEventListener('click', handleGlobalClick, true);
  }, [isRecordingShadow]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        if (isRecordingShadow) {
          handleStopRecording();
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, isRecordingShadow]);

  const handleStopRecording = async () => {
    if (shadowEvents.length > 0) {
      const isElectron = typeof window !== 'undefined' && !!(window as any).wispr?.isElectron;
      if (isElectron) {
        // Electron: use wispr bridge
        try {
          const res = await (window as any).wispr.shadowRecord(shadowEvents, "Recorded Session");
          console.log("[Shadow] Recording saved (Electron):", res);
        } catch (e) { console.error("[Shadow] Electron save failed:", e); }
      } else {
        // Web: POST to shadow-mode-service via orbit-gateway
        try {
          const { getAuth } = await import('firebase/auth');
          const auth = getAuth();
          const token = await auth.currentUser?.getIdToken();
          const gatewayUrl = (import.meta as any).env?.VITE_ORBIT_GATEWAY_URL || 'https://orbit-gateway-buzbhnk2ha-uc.a.run.app';
          const resp = await fetch(`${gatewayUrl}/shadow/record`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              user_id: auth.currentUser?.uid || 'anonymous',
              intent: 'Recorded Session',
              events: shadowEvents,
            }),
          });
          const data = await resp.json();
          console.log("[Shadow] Recording saved (web):", data);
          // Show the user what was recorded
          if (data.compiled_steps && data.compiled_steps.length > 0) {
            onSubmit(`I just recorded a routine with ${data.compiled_steps.length} steps. Here's what I did: ${data.compiled_steps.map((s: any) => s.description || s.action || '').join(', ')}. Can you turn this into a reusable automation?`);
          }
        } catch (e) { console.error("[Shadow] Web save failed:", e); }
      }
    }
    setShadowEvents([]);
    stopShadowRecord();
  };

  const handleSubmit = () => {
    if (inputText.trim()) {
      onSubmit(inputText.trim());
      setInputText('');
      setScreenshot(null);
      onClose();
    }
  };


  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center pb-32"
      onClick={onClose}
    >
      {/* Backdrop with blur */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Spotlight Container */}
      <div
        className="ghost-spotlight-ui relative w-full max-w-2xl mx-4 animate-in fade-in slide-in-from-bottom-4 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Main Glass Card */}
        <div className={`bg-black/90 backdrop-blur-[60px] border-2 rounded-3xl overflow-hidden transition-all duration-300 transform ${isRecordingShadow ? 'border-red-500/60 shadow-[0_0_80px_rgba(239,68,68,0.3),0_0_150px_rgba(239,68,68,0.15),inset_0_1px_10px_rgba(239,68,68,0.2)]' : 'border-cyan-400/40 shadow-[0_0_80px_rgba(34,211,238,0.2),0_0_150px_rgba(34,211,238,0.1),inset_0_1px_10px_rgba(34,211,238,0.1)]'}`}>

          {/* Deep Vision Screen Context Preview */}
          {screenshot && (
            <div className={`mx-3 mt-3 relative h-40 overflow-hidden rounded-2xl border ${isRecordingShadow ? 'border-red-500/40 shadow-[0_0_20px_rgba(239,68,68,0.2)]' : 'border-cyan-400/30 shadow-[0_0_20px_rgba(34,211,238,0.1)]'} group`}>
              <img src={screenshot} alt="Visual Context" className={`w-full h-full object-cover opacity-70 transition-opacity duration-500 ${isRecordingShadow ? 'blur-none' : 'group-hover:opacity-100 blur-[1px] group-hover:blur-none'}`} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex items-end justify-between p-3">
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full animate-pulse ${isRecordingShadow ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,1)]' : 'bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,1)]'}`} />
                  <span className={`text-[10px] font-bold tracking-wider uppercase drop-shadow-[0_0_2px_rgba(0,0,0,1)] ${isRecordingShadow ? 'text-red-400' : 'text-cyan-400'}`}>
                    {isRecordingShadow ? 'Recording Shadow Mode...' : 'Deep Vision Active'}
                  </span>
                </div>
                {isRecordingShadow ? (
                  <Activity className="w-4 h-4 text-red-500 animate-pulse drop-shadow-[0_0_2px_rgba(0,0,0,1)]" />
                ) : (
                  <Ghost className="w-4 h-4 text-cyan-400/60 drop-shadow-[0_0_2px_rgba(0,0,0,1)]" />
                )}
              </div>
            </div>
          )}

          <div className="flex items-center p-4 gap-3">
            {/* Action Icon */}
            <div className={`p-2.5 rounded-xl border ${isRecordingShadow ? 'bg-red-500/10 border-red-500/40' : 'bg-cyan-400/10 border-cyan-400/30'}`}>
              {isRecordingShadow ? (
                <div className="w-4 h-4 rounded-full bg-red-500 shadow-[0_0_12px_rgba(239,68,68,1)] animate-pulse" />
              ) : (
                <Ghost className="w-5 h-5 text-cyan-400 drop-shadow-[0_0_8px_rgba(34,211,238,0.8)]" />
              )}
            </div>


            {/* Input Field / Status */}
            {isRecordingShadow ? (
              <div className="flex-1 px-2 flex items-center gap-3">
                <span className="text-red-400 font-bold tracking-widest uppercase text-xs drop-shadow-[0_0_4px_rgba(239,68,68,0.6)] animate-pulse">
                  Recording Routine
                </span>
                <span className="text-red-400/50 text-sm font-medium">Perform actions naturally to translate to script...</span>
              </div>
            ) : (
              <input
                ref={inputRef}
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
                placeholder="What can SuperJarvis do for you?"
                className="flex-1 bg-transparent border-none outline-none text-cyan-300 placeholder-cyan-400/40 text-base font-medium"
              />
            )}

            {/* Mode Badge / Timer */}
            {isRecordingShadow && (
              <div className="flex flex-col items-center justify-center px-4">
                <div className="text-[10px] font-bold text-red-500 uppercase tracking-widest drop-shadow-[0_0_2px_rgba(239,68,68,0.8)] blink">REC</div>
              </div>
            )}

            {!isRecordingShadow && (
              <>
                {/* Mic */}
                <button className="p-2 rounded-lg hover:bg-cyan-400/10 text-cyan-400/50 hover:text-cyan-400 transition-colors">
                  <Mic className="w-4 h-4" />
                </button>

                {/* Live/Radio */}
                <button className="p-2 rounded-lg hover:bg-cyan-400/10 text-cyan-400/50 hover:text-cyan-400 transition-colors">
                  <Radio className="w-4 h-4" />
                </button>

                {/* Mode Badge Label */}
                <div className="hidden sm:block">
                  <div className="text-[9px] font-semibold text-cyan-400/60 uppercase tracking-widest pl-1">Ghost Layer</div>
                </div>

                {/* Screen Share / Shadow */}
                <button className="p-2 rounded-lg bg-cyan-400/20 border border-cyan-400/40 text-cyan-400 hover:bg-cyan-400/30 hover:shadow-[0_0_15px_rgba(34,211,238,0.5)] transition-all">
                  <Ghost className="w-4 h-4 animate-pulse" />
                </button>
              </>
            )}

            {/* Divider */}
            <div className={`w-px h-6 ${isRecordingShadow ? 'bg-red-500/30' : 'bg-cyan-400/20'}`} />

            {/* Send / Stop Button */}
            {isRecordingShadow ? (
              <button
                onClick={handleStopRecording}
                className="px-4 py-2 rounded-lg bg-red-500/20 border border-red-500/50 text-red-400 font-bold text-xs uppercase hover:bg-red-500 hover:text-white transition-all shadow-[0_0_20px_rgba(239,68,68,0.4)]"
              >
                Stop
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!inputText.trim()}
                className={`p-2 rounded-lg transition-all ${inputText.trim()
                  ? 'bg-cyan-400 text-black shadow-[0_0_20px_rgba(34,211,238,0.5)] hover:shadow-[0_0_30px_rgba(34,211,238,0.7)]'
                  : 'bg-cyan-400/10 text-cyan-400/30 cursor-not-allowed'
                  }`}
              >
                <ArrowUp className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Hint Text */}
        <p className={`text-center text-xs mt-3 tracking-wide ${isRecordingShadow ? 'text-red-400/50' : 'text-cyan-400/30'}`}>
          {isRecordingShadow ? (
            <>Press <kbd className="px-1.5 py-0.5 bg-red-500/10 rounded text-red-400/80 font-mono">Esc</kbd> to stop recording</>
          ) : (
            <>Press <kbd className="px-1.5 py-0.5 bg-cyan-400/10 rounded text-cyan-400/50 font-mono">Esc</kbd> to close · <kbd className="px-1.5 py-0.5 bg-cyan-400/10 rounded text-cyan-400/50 font-mono">Ctrl+Z</kbd> to toggle</>
          )}
        </p>
      </div>
    </div>
  );
};

// Status Dot Component
export const StatusDot = ({ status }: { status: 'running' | 'pending' | 'cancelled' | 'completed' | 'paused' }) => {
  const colors = {
    running: 'bg-green-400 shadow-[0_0_10px_rgba(74,222,128,0.8)]',
    pending: 'bg-yellow-400 shadow-[0_0_10px_rgba(250,204,21,0.8)]',
    cancelled: 'bg-red-400 shadow-[0_0_10px_rgba(248,113,113,0.8)]',
    completed: 'bg-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.8)]',
    paused: 'bg-orange-400 shadow-[0_0_10px_rgba(251,146,60,0.8)]'
  };

  return (
    <div className={`w-3 h-3 rounded-full ${colors[status]} animate-pulse`} />
  );
};

// Export all icons for use in other components
export {
  Menu,
  Search,
  User,
  Mic,
  Plus,
  Zap,
  Ghost,
  Grid,
  Briefcase,
  X,
  ChevronRight,
  ChevronLeft,
  ImageIcon,
  Video,
  FileText,
  Smartphone,
  Users,
  Radio,
  Activity,
  Calendar,
  Mail,
  MessageCircle,
  Share2,
  Globe,
  Layout,
  Cpu,
  Clock,
  Pin,
  Trash2,
  FolderPlus,
  MoreVertical,
  Link,
  Upload,
  Play,
  Pause,
  Check,
  CheckSquare,
  AlertCircle,
  XCircle,
  RefreshCw,
  Edit,
  Send,
  ArrowUp,
  Loader,
  Cloud,
  Sun,
  Car,
  TrendingUp,
  Bell,
  AlertTriangle,
  CheckCircle,
  Sparkles
};
